import { defineStore } from 'pinia';

async function sha256(message) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

interface EcommercePixelState {
    // You can add any state properties here if needed
}

export const useEcommercePixel = defineStore('ecommercePixel', {
    state: (): EcommercePixelState => ({
        // Define any state properties here if needed
    }),

    getters: {
        // Define any getters here if needed
    },

    actions: {
        // facebook
        viewProductFbq(data) {
            console.log('Facebook Pixel - ViewContent:', data);
            try {
                // Facebook Pixel's correct method is 'track'
                if (typeof data.analytics === 'function') {
                    data.analytics("track", "ViewContent", {
                        content_ids: data.product.parentSku
                            ? data.product.parentSku
                            : data.product.sku,
                        content_name: data.product.name,
                        contents: [{ id: data.product.parentSku, quantity: 1 }],
                        content_type: "product",
                        value: data?.product?.final_price ? data?.product?.final_price : data?.product?.original_price,
                        currency: "VND",
                    });
                } else {
                    console.error('Facebook Pixel tracking not available: analytics is not a function');
                }
            } catch (error) {
                console.error('Error in Facebook Pixel ViewContent tracking:', error);
            }
        },

        async addToCartFbq(data) {
            try {
                let email = null;
                let phone = null;
                if (data?.userData) {
                    email = await sha256(data?.userData?.email);
                    let phoneData = data?.userData?.custom_attributes?.find((e) => e.attribute_code == 'phone_number')?.value;
                    if (phoneData) {
                        phone = await sha256(phoneData);
                    }
                }

                // Use track method for fbq
                if (typeof data.analytics === 'function') {
                    data.analytics("track", "AddToCart", {
                        content_ids: data.product.parentSku
                            ? data.product.parentSku
                            : data.product.sku,
                        content_name: data.product.name,
                        em: email ? email : null,
                        ph: phone ? phone : null,
                        external_id: data?.userData?.id ? data?.userData?.id : null,
                        content_type: 'product',
                        value: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                        currency: 'VND'
                    });
                    console.log('Facebook Pixel - AddToCart:', data);

                } else {
                    console.error('Facebook Pixel tracking not available: analytics is not a function');
                }
            } catch (error) {
                console.error('Error in Facebook Pixel AddToCart tracking:', error);
            }
        },

        async initCheckoutFbq(data) {
            try {
                let email = null;
                let phone = null;
                if (data?.stock?.userData) {
                    email = await sha256(data?.stock?.userData?.email);
                    let phoneData = data?.stock?.userData?.custom_attributes?.find((e) => e.attribute_code == 'phone_number')?.value;
                    if (phoneData) {
                        phone = await sha256(phoneData);
                    }
                }

                // Use track method for fbq
                if (typeof data.analytics === 'function') {
                    data.analytics("track", "InitiateCheckout", {
                        content_type: "product",
                        em: email ? email : null,
                        ph: phone ? phone : null,
                        external_id: data?.stock?.userData?.id ? data?.stock?.userData?.id : null,
                        content_ids: data.stock.content_ids,
                        contents: data.stock.contentsFbq,
                        content_name: data.stock.content_name,
                        number_items: data.stock.number_items,
                        value: data.stock.value,
                        currency: "VND",
                    });
                    console.log('Facebook Pixel - InitiateCheckout:', data);
                } else {
                    console.error('Facebook Pixel tracking not available: analytics is not a function');
                }
            } catch (error) {
                console.error('Error in Facebook Pixel InitiateCheckout tracking:', error);
            }
        },

        async purchaseFbq(data) {
            try {
                let email = null;
                let phone = null;
                if (data?.userData) {
                    email = await sha256(data?.userData?.email);
                    let phoneData = data?.userData?.custom_attributes?.find((e) => e.attribute_code == 'phone_number')?.value;
                    if (phoneData) {
                        phone = await sha256(phoneData);
                    }
                }

                // Use track method for fbq
                if (typeof data.analytics === 'function') {
                    data.analytics("track", "Purchase", {
                        em: email ? email : null,
                        ph: phone ? phone : null,
                        external_id: data?.userData?.id ? data?.userData?.id : null,
                        content_type: 'product',
                        contents: data.order.contents,
                        content_ids: data.order.contents.map(item => item.id),
                        value: data.order.value,
                        currency: 'VND',
                        transaction_id: data.order.id
                    });
                    console.log('Facebook Pixel - Purchase:', data);
                } else {
                    console.error('Facebook Pixel tracking not available: analytics is not a function');
                }
            } catch (error) {
                console.error('Error in Facebook Pixel Purchase tracking:', error);
            }
        },

        // Gtag
        viewProductGtag(data) {
            console.log('Google Tag - ViewProduct:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "view-product",
                ecommerce: {
                    detail: {
                        products: [
                            {
                                title: data.product?.name,
                                name: data.product?.name,
                                description: data.product.description,
                                link: `${process.env.VSF_STORE_URL}/${data.product.url_path}`,
                                image_link: data.product?.image && data.product?.image?.length > 0 ? `${process.env.VSF_STORE_URL}/img/212/284/resize/${data.product?.image[0]?.image}` : data.product?.image,
                                availability: "in stock",
                                item_group_id: data.product.parentSku,
                                condition: "new",
                                sale_price: data.product.original_price - data.product.final_price,
                                brand: "CANIFA",
                                id: data.product.parentSku,
                                price: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                            },
                        ],
                    },
                },
            });
        },

        addToCartGtag(data) {
            console.log('Google Tag - AddToCart:', data);
            data.dataLayer.push({ ecommerce: null }); //Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "addToCart",
                ecommerce: {
                    currencyCode: "VND",
                    add: {
                        //'add' actionFieldObject measures.
                        products: [
                            {
                                //adding a product to a shopping cart.
                                title: data.product?.name,
                                name: data.product?.name,
                                description: data.product.description,
                                link: `${process.env.VSF_STORE_URL}/${data.product.url_path}`,
                                image_link: data.product?.image && data.product?.image?.length > 0 ? `${process.env.VSF_STORE_URL}/img/212/284/resize/${data.product?.image[0]?.image}` : data.product?.image,
                                availability: 'in stock',
                                item_group_id: data.product.parentSku,
                                condition: 'new',
                                sale_price: data.product.original_price - data.product.final_price,
                                brand: 'CANIFA',
                                id: data.product.parentSku,
                                price: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                            },
                        ],
                    },
                },
            });
        },

        viewCategoryGtag(data) {
            console.log('Google Tag - ViewCategory:', data);
            let newArr = [];
            data?.allproduct?.forEach((element) => {
                newArr.push({
                    title: element.title,
                    description: element.description,
                    link: `${process.env.VSF_STORE_URL}/${element.link}`,
                    image_link: element.image,
                    availability: "in stock",
                    item_group_id: element.parentSku,
                    condition: "new",
                    brand: "CANIFA",
                    sale_price: element?.original_price - element?.final_price,
                    id: element.parentSku,
                    price: element?.price_incl_tax,
                });
            });
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "view-category",
                ecommerce: {
                    impressions: newArr,
                },
            })
        },

        initCheckoutGtag(data) {
            console.log('Google Tag - InitiateCheckout:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "checkout",
                ecommerce: {
                    checkout: {
                        products: data.stock,
                    },
                },
            });
        },

        purchaseGtag(data) {
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "purchase",
                ecommerce: {
                    actionField: {
                        id: data.order.transaction_id,// Transaction ID. Required for purchases and refunds.
                        affiliation: "CANIFA Website",
                        revenue: data.order.value,// Total transaction value (incl. tax and shipping)
                        tax: data.order.tax,
                        shipping: data.order.shipping,
                        coupon: data.order.coupon,
                    },
                    products: data.order.items.map(item => ({
                        name: item.item_name,
                        id: item.parent_sku,
                        price: item.price,
                        brand: item.item_brand || "CNF",
                        category: item.item_category || "",
                        variant: item.item_variant || "",
                        quantity: item.quantity,
                        item_id_custom: item.parent_sku || item.item_id
                    })),
                }
            });
            console.log('Google Tag - Purchase:', data);
        },

        // gtags retail
        viewProductGtagRetail(data) {
            console.log('Google Tag Retail - ViewProduct:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                dr_event_type: "view_item",
                dr_value: data.product?.final_price ? data.product?.final_price : data.product?.original_price, // product price
                dr_items: [
                    {
                        id: data.product.parentSku, // should be the same as id in Google Merchant Center (mã sku ví dụ: 8TW21W009),
                        google_business_vertical: "retail",
                    },
                ],
                event: "dynamic_remarketing",
            });
        },

        addToCartGtagRetail(data) {
            console.log('Google Tag Retail - AddToCart:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                dr_event_type: "add_to_cart",
                dr_value: data.product?.final_price ? data.product?.final_price : data.product?.original_price, // product price
                dr_items: [
                    {
                        id: data.product.parentSku, // should be the same as id in Google Merchant Center (mã sku ví dụ: 8TW21W009),
                        google_business_vertical: "retail",
                    },
                ],
                event: "dynamic_remarketing",
            });
        },

        purchaseGa4(data) {
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "purchase",
                ecommerce: {
                    transaction_id: data.order.transaction_id,
                    value: data.order.value,
                    tax: data.order.tax,
                    shipping: data.order.shipping,
                    currency: "VND",
                    coupon: data.order.coupon || "",
                    items: data.order.items
                }
            });
            console.log('Google Tag Ga4 - Purchase:', data);
        },

        // tiktok
        viewProductTiktok(data) {
            try {


                data.ttq.track("ViewContent", {
                    content_id: data.product.parentSku
                        ? data.product.parentSku
                        : data.product.sku,
                    content_name: data.product.name,
                    content_type: "product",
                    price: data?.product?.final_price ? data?.product?.final_price : data?.product?.original_price,
                    currency: "VND",
                });
 
                console.log('TikTok Pixel - ViewContent (before tracking):', {
                    content_id: data.product.parentSku || data.product.sku,
                    content_name: data.product.name,
                    price: data?.product?.final_price || data?.product?.original_price
                });
            } catch (error) {
                console.error('TikTok Pixel - ViewContent (error):', error);
            }
        },

        addToCartTiktok(data) {
            console.log('TikTok Pixel - AddToCart:', data);
            data.ttq.track('AddToCart', {
                contents: [
                    {
                        content_id: data.product.parentSku,
                        content_type: 'product',
                        content_name: data.product.name,
                        quantity: 1,
                        price: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                    }],
                value: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                currency: 'VND',
            });
        },

        initCheckoutTiktok(data) {
            console.log('TikTok Pixel - InitiateCheckout:', data);
            data.ttq.track('InitiateCheckout', {
                contents: data.stock.contents,
                value: data.stock.value, // tổng giá trị của tất cả sản  phẩm khi user đang ở trang điền thông tin checkout
                currency: 'VND',
            });
        },

        purchaseTiktok(data) {
            data.ttq.track('CompletePayment', {
                contents: data.order.contents,
                value: data.order.value,
                currency: 'VND',
                content_type: 'product',
                content_id: data.order.order_id || data.order.transaction_id
            });
            console.log('TikTok Pixel - CompletePayment:', data);
        },

        //ga4
        logintGa4(data) {
            console.log('GA4 - Login:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "login",
                ecommerce: {
                    method: "OTP"
                },
            });
        },

        viewProductGa4(data) {
            console.log('GA4 - ViewItem:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "view_item",
                ecommerce: {
                    items: [
                        {
                            item_name: data.product?.name,
                            item_id: data.product?.parentSku,
                            item_id_custom: data.product?.parentSku,
                            discount: data.product.original_price - data.product.final_price,
                            item_brand: "CANIFA",
                            price: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                            quantity: 1,
                            currency: 'VND'
                        },
                    ],
                },
            });
        },

        addToCartGa4(data) {
            console.log('GA4 - AddToCart:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "add_to_cart",
                ecommerce: {
                    items: [
                        {
                            item_id: data.product.parentSku,
                            item_id_custom: data.product.parentSku,
                            item_name: data.product?.name,
                            currency: "VND",
                            discount: data.product.original_price - data.product.final_price,
                            item_brand: "CANIFA",
                            price: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                            quantity: 1
                        }
                    ]
                },
            });
        },

        initCheckoutGa4(data) {
            let newItems = []
            data.stock.forEach(element => {
                newItems.push({
                    item_name: element?.title,
                    item_id: element.parent_sku,
                    discount: element.sale_price,
                    item_id_custom: element.parent_sku,
                    item_brand: "CANIFA",
                    price: element.price,
                    quantity: element.qty,
                    currency: 'VND'
                })
            });
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "begin_checkout",
                ecommerce: {
                    items: newItems
                },
            });
        },

        addToWishGa4(data) {
            console.log('GA4 - AddToWishlist:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "add_to_wishlist",
                ecommerce: {
                    items: [
                        {
                            item_id: data.product.parentSku,
                            item_name: data.product?.name,
                            currency: "VND",
                            discount: data.product.original_price - data.product.final_price,
                            item_brand: "CANIFA",
                            price: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                            quantity: 1
                        }
                    ]
                },
            });
        },

        viewCartGa4(data) {
            console.log('GA4 - ViewCart:', data);
            let newItems = []
            data.stock.forEach(element => {
                newItems.push({
                    item_name: element?.title,
                    item_id: element.id,
                    discount: element.sale_price,
                    item_brand: "CANIFA",
                    price: element.price,
                    quantity: element.qty,
                    currency: 'VND'
                })
            });
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "view_cart",
                ecommerce: {
                    items: newItems
                },
            });
        },

        removeFromCartGa4(data) {
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "remove_from_cart",
                ecommerce: {
                    items: [
                        {
                            item_id: data.product.parentSku,
                            item_name: data.product?.name,
                            currency: "VND",
                            discount: data.product.original_price - data.product.final_price,
                            item_brand: "CANIFA",
                            price: data.product?.final_price ? data.product?.final_price : data.product?.original_price,
                            quantity: data.product?.qty ? data.product?.qty : 1
                        }
                    ]
                },
            });
            console.log('GA4 - RemoveFromCart:', data);

        },

        loginGa4(data) {
            console.log('GA4 - Login with method:', data);
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "login",
                loginMethod: "OTP",
            });
        },

        shippingInforGa4(data) {
            console.log('GA4 - Shipping Info:', data);
            let newItems = []
            data.stock.forEach(element => {
                newItems.push({
                    item_name: element?.title,
                    item_id: element.id,
                    discount: element.sale_price,
                    item_brand: "CANIFA",
                    price: element.price,
                    quantity: element.qty,
                    currency: 'VND'
                })
            });
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            data.window_dataLayer.push({
                event: "view_cart",
                ecommerce: {
                    items: newItems,
                    payment_type: "Credit Card",
                    coupon: "SUMMER_FUN",
                    currency: "USD",
                    value: 7.77,
                },
            });
        },

        paymentInforGa4(data) {
            data.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            let newItems = []
            let total = 0
            data.stock.forEach(element => {
                newItems.push({
                    item_name: element?.name,
                    item_id: element?.parentSku,
                    discount: element?.original_price - element?.final_price,
                    item_brand: "CANIFA",
                    price: element?.final_price,
                    quantity: element.qty,
                    currency: 'VND'
                })
                total += element.qty * element.final_price
            });
            data.window_dataLayer.push({
                event: "add_payment_info",
                ecommerce: {
                    items: newItems,
                    payment_type: data.payment,
                    currency: "VND",
                    value: total,
                },
            });
            console.log('GA4 - Payment Info:', data);

        },
    }
});
